import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9pmmi6b4qjhggaeq1ag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
        if (serverMsg.toLowerCase().includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg || '提交失败，请稍后重试');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '91自拍论坛',
    url: 'https://www.91selfie.com/',
    description: '摄影爱好者自拍交流社区',
    email: 'contact@91selfie.com',
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      streetAddress: '摄影创意园A座',
      postalCode: '100020',
      addressCountry: 'CN',
    },
  };

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 91自拍论坛',
    description: '了解91自拍论坛的品牌故事、价值观和联系方式',
    url: 'https://www.91selfie.com/about',
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Header */}
          <section className="relative w-full h-[280px] md:h-[360px] flex items-center overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20and%20inviting%20creative%20photography%20community%20space%20with%20gallery%20wall%20displaying%20beautiful%20photos%2C%20modern%20minimalist%20interior%20with%20wooden%20furniture%2C%20soft%20natural%20lighting%2C%20cream%20and%20beige%20color%20palette%2C%20artistic%20atmosphere%2C%20community%20hub%20vibe&width=1600&height=700&seq=about-header&orientation=landscape"
              alt="关于91自拍论坛"
              title="关于91自拍论坛"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/50"></div>
            <div className="relative z-10 w-full px-4 md:px-6 max-w-7xl mx-auto pt-16">
              <div className="mb-4">
                <Breadcrumb items={[{ label: '关于我们' }]} />
              </div>
              <h1 className="font-heading text-3xl md:text-4xl text-background-50">关于我们</h1>
            </div>
          </section>

          {/* Brand Story */}
          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-4">
                  品牌故事
                </h2>
                <div className="w-16 h-1 bg-primary-500 mx-auto rounded-full mb-6"></div>
              </div>
              <div className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-5">
                <p>
                  91自拍论坛诞生于一群摄影爱好者对自拍艺术的共同追求。2020年，几位热爱手机摄影的朋友聚在一起，发现市场上缺乏一个专注于自拍技巧分享的垂直社区。于是，91自拍论坛应运而生。
                </p>
                <p>
                  从最初只有几十人的小圈子，到如今拥有超过五万名注册会员的活跃社区，91自拍论坛始终坚持一个核心理念：<strong>用手机也能拍出艺术级的自拍作品</strong>。
                </p>
                <p>
                  我们相信，好的自拍不是依赖昂贵的设备，而是源于对构图、光线和色彩的理解。每一位摄影爱好者都值得拥有记录美好自己的能力，而我们的使命，就是帮助你掌握这项能力。
                </p>
              </div>
            </div>
          </section>

          {/* Mission & Values */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-4">
                  我们的价值观
                </h2>
                <p className="text-sm text-foreground-500 max-w-lg mx-auto">
                  指引我们前行的核心信念
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {[
                  {
                    icon: 'ri-lightbulb-line',
                    title: '知识共享',
                    desc: '我们坚信知识应该自由流动。所有教程免费开放，让每一位摄影爱好者都能无障碍地学习和成长。',
                  },
                  {
                    icon: 'ri-group-line',
                    title: '社区驱动',
                    desc: '社区的力量来自每一位成员的分享与互动。我们鼓励达人分享经验，新手积极提问，共同构建有温度的学习环境。',
                  },
                  {
                    icon: 'ri-star-line',
                    title: '品质至上',
                    desc: '每一篇教程都经过严格审核，确保内容的准确性和实用性。我们追求的不是数量，而是每一篇文章的品质。',
                  },
                  {
                    icon: 'ri-heart-line',
                    title: '包容多元',
                    desc: '无论你用什么设备、是什么水平，这里都有你的位置。我们尊重每一种拍摄风格和审美偏好。',
                  },
                  {
                    icon: 'ri-rocket-line',
                    title: '持续创新',
                    desc: '摄影技术在不断演进，我们的内容和方法也在持续更新。从AI修图到手机新功能，我们始终走在最前沿。',
                  },
                  {
                    icon: 'ri-shield-check-line',
                    title: '尊重原创',
                    desc: '我们严格保护原创作者的权益，每一篇教程都标注作者信息，转载必须获得授权。',
                  },
                ].map((value, i) => (
                  <div
                    key={i}
                    className="bg-background-50 rounded-lg p-6 border border-background-200/70"
                  >
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                      <i className={`${value.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-heading text-base text-foreground-900 mb-2">{value.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{value.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Reputation */}
          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-4">
                  社区口碑
                </h2>
                <p className="text-sm text-foreground-500 max-w-lg mx-auto">
                  听听社区成员怎么说
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                {[
                  {
                    name: '摄影爱好者小王',
                    text: '91自拍论坛是我入门的引路人。从完全不懂构图的小白，到现在能拍出让朋友惊叹的自拍，只用了一个月时间。教程写得通俗易懂，真的很棒！',
                    role: '社区成员 · 加入1年',
                  },
                  {
                    name: '旅行博主小美',
                    text: '作为一个经常独自旅行的人，自拍是必备技能。91自拍论坛的旅行自拍教程改变了我的旅行记录方式，现在每条动态的点赞都翻了一倍。',
                    role: '社区达人 · 加入2年',
                  },
                  {
                    name: '大学生阿杰',
                    text: '后期修图板块简直是我的救星！之前总觉得自己的照片差了点什么，学了调色教程之后才发现，原来色彩可以这么神奇。',
                    role: '社区成员 · 加入6个月',
                  },
                  {
                    name: '摄影师老张',
                    text: '虽然我从事专业摄影多年，但91自拍论坛的很多手机摄影技巧依然让我眼前一亮。手机摄影的便捷性和创造力远超很多人的想象。',
                    role: '签约达人 · 加入3年',
                  },
                ].map((review, i) => (
                  <div
                    key={i}
                    className="bg-background-100 rounded-lg p-6 border border-background-200/70"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="font-heading text-primary-600 text-sm">{review.name.charAt(0)}</span>
                      </div>
                      <div>
                        <div className="font-medium text-sm text-foreground-800">{review.name}</div>
                        <div className="text-xs text-foreground-400">{review.role}</div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">&ldquo;{review.text}&rdquo;</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-2xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-500">
                  有任何问题或建议，欢迎随时与我们联系
                </p>
              </div>

              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-background-50 rounded-lg p-6 md:p-8 border border-background-200/70"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    id="contact-subject"
                    name="subject"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="请输入主题"
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言 <span className="text-accent-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={5}
                    required
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                    placeholder="请输入您的留言（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
                </div>

                {/* Honeypot */}
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="hp-field"
                />

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-background-50 rounded-lg font-medium text-sm whitespace-nowrap cursor-pointer transition-colors"
                >
                  {formStatus === 'submitting' ? '提交中...' : '发送留言'}
                </button>

                {formStatus === 'success' && (
                  <p className="mt-4 text-sm text-center text-green-600 font-medium">
                    感谢您的留言！我们会尽快回复您。
                  </p>
                )}
                {formStatus === 'error' && formError && (
                  <p className="mt-4 text-sm text-center text-accent-600 font-medium">
                    {formError}
                  </p>
                )}
              </form>
            </div>
          </section>

          {/* Contact Info */}
          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6">
                <div className="text-center p-6">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mx-auto mb-3">
                    <i className="ri-mail-line text-2xl"></i>
                  </div>
                  <h3 className="font-heading text-base text-foreground-900 mb-1">电子邮箱</h3>
                  <p className="text-sm text-foreground-500">contact@91selfie.com</p>
                </div>
                <div className="text-center p-6">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mx-auto mb-3">
                    <i className="ri-map-pin-line text-2xl"></i>
                  </div>
                  <h3 className="font-heading text-base text-foreground-900 mb-1">办公地址</h3>
                  <p className="text-sm text-foreground-500">北京市朝阳区摄影创意园A座</p>
                </div>
                <div className="text-center p-6">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mx-auto mb-3">
                    <i className="ri-wechat-line text-2xl"></i>
                  </div>
                  <h3 className="font-heading text-base text-foreground-900 mb-1">微信公众号</h3>
                  <p className="text-sm text-foreground-500">91自拍论坛</p>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}